// Light mode colors
const lightColors = {
  // LINE brand colors
  primary: '#00A960',
  primaryLight: '#42C142',
  primaryDark: '#007B00',

  // Application-specific brand colors
  teal: {
    light: '#EAF7F8',
    main: '#00B2B2',
    dark: '#008A8A',
  },
  blue: {
    light: '#E3EDFF',
    main: '#2762E6',
    dark: '#1A4DB8',
  },
  orange: {
    light: '#FFF3E6',
    main: '#D95F00',
    dark: '#BF5400',
    darker: '#A64A00',
  },

  // UI colors
  pageBackground: '#F5F6F9',
  background: '#FFFFFF',
  backgroundSecondary: '#F8F9FA',
  backgroundAccent: '#E8F5E8',
  backgroundTertiary: '#F1F3F5',
  backgroundTableHeader: '#F6F6F6',
  backgroundTableGood: '#EEFFF6',
  backgroundTableDanger: '#FEF3F2',
  backgroundTableWarning: '#FFF2CC',
  backgroundSupplementary: '#6D6D6D',

  text: {
    primary: '#4D4D4D',
    secondary: '#6D6D6D',
    tertiary: '#495057',
    muted: '#999999',
    light: '#868E96',
    lighter: '#ADB5BD',
    inverse: '#FFFFFF',
    danger: '#DE3624',
    sunday: '#F15342',
    saturday: '#4188F2',
    icon: '#888888',
  },

  border: {
    light: '#E5E5E5',
    medium: '#CCCCCC',
    dark: '#999999',
    darker: '#CED4DA',
    input: '#BEBEBE',
    separator: '#DEE2E6',
    supplementary: '#6D6D6D',
    driver: '#D6D6D6',
    option: '#D1D1D1', // 招待リンクの役割オプション選択枠（Figma指定）
  },

  status: {
    success: '#28A745',
    warning: '#FFC107',
    error: '#DC3545',
    info: '#17A2B8',
  },

  alert: {
    normal: '#28A745',
    attention: '#FFC107',
    urgent: '#DC3545',
  },

  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #00B900 0%, #007B00 100%)',
    teal: 'linear-gradient(135deg, #00B2B2 0%, #008A8A 100%)',
  },

  // Admin-specific colors
  admin: {
    sidebarBg: '#FFFFFF',
    dashboardBg: '#F8F9FA',
  }
};

// Dark mode colors
const darkColors = {
  // LINE brand colors (slightly adjusted for dark mode)
  primary: '#00C700',
  primaryLight: '#42D142',
  primaryDark: '#009000',

  // Application-specific brand colors
  teal: {
    light: '#1A3A3B',
    main: '#00C2C2',
    dark: '#00A0A0',
  },
  blue: {
    light: '#1A2A3F',
    main: '#3070FF',
    dark: '#2055CC',
  },
  orange: {
    light: '#3A2A1A',
    main: '#E96F00',
    dark: '#CC6000',
    darker: '#B65500',
  },

  // UI colors
  pageBackground: '#1A1A1A',
  background: '#2A2A2A',
  backgroundSecondary: '#2A2A2A',
  backgroundAccent: '#1A3A1A',
  backgroundTertiary: '#252525',
  backgroundTableHeader: '#3A3A3A',
  backgroundTableGood: '#1A3A1A',
  backgroundTableDanger: '#3A1A1A',
  backgroundTableWarning: '#3A3020',
  backgroundSupplementary: '#808080',

  text: {
    primary: '#E5E5E5',
    secondary: '#A0A0A0',
    tertiary: '#B0B0B0',
    muted: '#808080',
    light: '#909090',
    lighter: '#707070',
    inverse: '#1A1A1A',
    danger: '#F44336',
    sunday: '#F15342',
    saturday: '#4188F2',
    icon: '#888888',
  },

  border: {
    light: '#3A3A3A',
    medium: '#4A4A4A',
    dark: '#606060',
    darker: '#454545',
    input: '#454545',
    separator: '#353535',
    supplementary: '#6D6D6D',
    driver: '#D6D6D6',
    option: '#4A4A4A', // 招待リンクの役割オプション選択枠（ダークテーマでは medium と同等）
  },

  status: {
    success: '#32C952',
    warning: '#FFD54F',
    error: '#F44336',
    info: '#29B6F6',
  },

  alert: {
    normal: '#32C952',
    attention: '#FFD54F',
    urgent: '#F44336',
  },

  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #00C700 0%, #009000 100%)',
    teal: 'linear-gradient(135deg, #00C2C2 0%, #00A0A0 100%)',
  },

  // Admin-specific colors
  admin: {
    sidebarBg: '#1E1E1E',
    dashboardBg: '#1A1A1A',
  }
};

export const theme = {
  colors: lightColors,
  
  spacing: {
    xs: '4px',
    sm: '8px',
    ms: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  
  typography: {
    fontFamily: {
      base: '"Hiragino Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      xxl: '24px',
      xxxl: '32px',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.6,
    },
  },
  
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50%',
  },
  
  shadows: {
    sm: '0 1px 2px var(--2, 2px) rgba(77, 77, 77, 0.08)',
    md: '0 4px 8px var(--2, 2px) rgba(77, 77, 77, 0.08)',
    lg: '0 8px 16px var(--2, 2px) rgba(77, 77, 77, 0.08)',
    xl: '0 16px 24px var(--2, 2px) rgba(77, 77, 77, 0.08)',
  },
  
  breakpoints: {
    xs: '320px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
  
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },
  
  // Accessibility settings
  accessibility: {
    focusOutline: '2px solid #00B900',
    focusOffset: '2px',
    minTouchTarget: '44px', // iOS/Android minimum
  },
};

export type Theme = typeof theme;

// Helper function to create theme with specific color mode
export const createTheme = (mode: 'light' | 'dark') => ({
  ...theme,
  colors: mode === 'dark' ? darkColors : lightColors,
});